import { SetOptional } from 'type-fest';

import {
  FormModal,
  FormModalProps,
} from '@/components/modals/FormModal/FormModal';
import { FormModalActions } from '@/components/modals/FormModal/FormModalActions/FormModalActions';
import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import { FormProvider, useFormContext } from '@/components/react-hook-form';
import { useForm, useSubmitSuccessHandler } from '@/components/react-hook-form';
import { useFormSaveHandler } from '@/hooks/useFormSaveHandler';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';

import { mapFormDataToInput } from './CreateTeamModal.utils';
import { useCreateTeamMutation } from './graphql/TeamModal.generated';
import { TeamForm } from './TeamForm';
import { getDefaultValues } from './TeamModal.constants';
import { TeamFormShape } from './TeamModal.types';

type FormModalOptionalProps = SetOptional<FormModalProps, 'heading'>;
export type CreateTeamModalProps = FormModalOptionalProps;

function CreateTeam({
  onClose,
  heading = 'Create team',
  ...formModalProps
}: CreateTeamModalProps) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { formRef, handleSave } = useFormSaveHandler();
  const trackUserEvent = useTrackUserEvent();
  const { handleSubmit, reset, shouldBlockNavigation, formState } =
    useFormContext<TeamFormShape>();

  const [createMutation] = useCreateTeamMutation({
    refetchQueries: 'active',
    awaitRefetchQueries: true,
    onError: (error) => {
      showFeedback(FeedbackMessages.formSaveError);
      reportError(`Could not create team`, error);
    },
    onCompleted: () => {
      trackUserEvent('team created');
      showFeedback('Successfully created team', { variant: 'success' });
    },
  });

  const onSubmit = handleSubmit((formData) => {
    trackUserEvent('team created', {
      assignedHouseholdCount: formData.assignedHouseholdIds.length,
    });

    const input = mapFormDataToInput(formData);

    return createMutation({ variables: { input } });
  });

  const closeModal = () => {
    reset();
    onClose();
  };

  useSubmitSuccessHandler(() => {
    closeModal();
  });

  return (
    <FormModal
      {...formModalProps}
      onClose={onClose}
      heading={heading}
      shouldBlockClose={shouldBlockNavigation}
      actions={
        <FormModalActions.Provider formState={formState}>
          <FormModalActions.CancelButton onClick={closeModal} />
          <FormModalActions.SaveButton onClick={handleSave}>
            Create team
          </FormModalActions.SaveButton>
        </FormModalActions.Provider>
      }
    >
      <form ref={formRef} onSubmit={onSubmit} noValidate>
        <TeamForm scimManaged={false} />
      </form>
    </FormModal>
  );
}

export const CreateTeamModal = ({ ...props }: CreateTeamModalProps) => {
  const formMethods = useForm<TeamFormShape>({
    defaultValues: {
      ...getDefaultValues(),
    },
  });

  return (
    <FormProvider {...formMethods}>
      <CreateTeam {...props} />
    </FormProvider>
  );
};
